import { useState, useEffect, useRef, useCallback } from "react";
import { Toast } from "primereact/toast";
import {
  getProductos,
  getPrecio,
  getRelacionados,
  eliminarProducto,
  actualizarCantidad,
  actualizarDescuento,
  actualizarEntrega,
  actualizarDespacho,
  actualizarPrecio,
  actualizarUnidad,
  actualizarCalzada,
  getAlmacen,
  existenciaCotizacion,
} from "../../../service/AffariService.js";
import TableProductos from "./TableProductos.js";
import TableDetalleCotizacion from "./TableDetalleCotizacion.js";
import { getdetalle, getResumen } from "../../../service/AffariService.js";
import BarraBusquedaCotizacion from "./BarraBusquedaCotizacion";
import ObservacionesEntregaResumen from "./ObservacionesEntregaResumen.js";
import LoaderTransparent from "../../../components/ui/LoaderTransparent.js";
import BotoneraCotizacion from "./BotoneraCotizacion.js";
import {
  mapDetalleResponseToLines,
  normalizeArticuloKey,
} from "../../../utilities/cotizacionUtils.js";
import { useOffline } from "../../../offline/OfflineContext.js";
import {
  listarDetallesCotizacionOffline,
  calcularResumenCotizacionOffline,
  actualizarDetalleCotizacionOffline,
  eliminarDetalleCotizacionOffline,
  actualizarCotizacionOffline,
} from "../../../offline/offlineCotizacionService.js";

const DetalleCotizacion = ({
  data,
  setData,
  idCotizacion,
  interfaz,
  isDetalleVisible,
  setIsDetalleVisible,
}) => {
  const [suggestions, setSuggestions] = useState({});
  const [invalidLines, setInvalidLines] = useState({});
  const [codigoBusqueda, setCodigoBusqueda] = useState("");
  const [descripcionBusqueda, setDescripcionBusqueda] = useState("");
  const [productosBusqueda, setProductosBusqueda] = useState([]);
  const toast = useRef(null);
  const proyectos = data.proyectos || [];
  const notify = useCallback((severity, summary, detail) => {
    if (toast.current) toast.current.show({ severity, summary, detail });
  }, []);
  const [visibleProductosTable, setVisibleProductosTable] = useState(false);
  const [detalles, setDetalles] = useState([]);
  const [relacionados, setRelacionados] = useState([]);
  const [resumen, setResumen] = useState({});
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [edit] = useState(false);
  const [existenciaStocks, setExistenciaStocks] = useState(null);
  const [almacenes, setAlmacenes] = useState([]);
  const [unidadMedidas, setUnidadMedidas] = useState([]);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [todosCajaCompleta, setTodosCajaCompleta] = useState(false);
  const [productoRecienAgregado, setProductoRecienAgregado] = useState(null);
  const { offlineMode } = useOffline();

  const fetchData = useCallback(async (forceFreshResumen = false, descuentoTotalOverride = null) => {
    try {
      setLoading(true);

      if (offlineMode) {
        const detallesOffline = await listarDetallesCotizacionOffline(
          idCotizacion,
        );
        const resumenOffline = await calcularResumenCotizacionOffline(
          idCotizacion,
        );

        setDetalles(detallesOffline);
        setRelacionados([]);
        setResumen(resumenOffline || {});

        return;
      }

      const detalleResponse = await getdetalle(
        false,
        false,
        idCotizacion,
        data.interfaz,
      );
      const serviciosResponse = detalleResponse?.servicios || {};
      const descuentoTotalActual = Number(
        descuentoTotalOverride ?? detalleResponse?.cotizacion?.descuento ?? data.descuentoTotal ?? 0,
      );
      setData((prev) => ({ ...prev, descuentoTotal: descuentoTotalActual }));
      const lines = mapDetalleResponseToLines(detalleResponse).map((line) => ({
        ...line,
        servicio:
          String(
            serviciosResponse?.[normalizeArticuloKey(line.referencia)] ??
              serviciosResponse?.[line.referencia] ??
              "false",
          ).toLowerCase() === "true",
      }));

      const unidadesResponse = Array.isArray(detalleResponse?.unidadMedidas)
        ? detalleResponse.unidadMedidas.filter(Boolean).map(String)
        : [];
      setUnidadMedidas(unidadesResponse);

      // cotizacion/detalle ya entrega los centros válidos por producto.
      // En RETAIL no se llamaba a existencia, por lo que el dropdown de Centro
      // quedaba sin opciones al confirmar.
      setAlmacenes(detalleResponse?.almacenes || {});

      const overrideLines =
        Array.isArray(lines) && existenciaStocks
          ? lines.map((line) => {
              const key = normalizeArticuloKey(line.referencia);
              const stockInfo = existenciaStocks[key];
              if (!stockInfo) return line;
              const nextStock =
                stockInfo.stock1 ?? stockInfo.stock ?? line.stock ?? 0;
              return { ...line, stock: nextStock };
            })
          : lines;

      setDetalles(overrideLines);
      setTodosCajaCompleta(
        overrideLines.length > 0 &&
          overrideLines.every((detalle) => detalle.cajaCompleta === true),
      );
      setSelectedProductIds((prev) =>
        prev.filter((id) => overrideLines.some((line) => line.id === id)),
      );

      let itemsRel = Array.isArray(detalleResponse?.relacionados)
        ? detalleResponse.relacionados
        : Array.isArray(detalleResponse?.relacionados?.items)
        ? detalleResponse.relacionados.items
        : null;

      if (!itemsRel) {
        const relacionadosResponse = await getRelacionados(idCotizacion);
        itemsRel = Array.isArray(relacionadosResponse)
          ? relacionadosResponse
          : Array.isArray(relacionadosResponse?.relacionados)
          ? relacionadosResponse.relacionados
          : [];
      }

      setRelacionados(itemsRel);

      if (interfaz === "MAYORISTA" && idCotizacion) {
        try {
          const existencia = await existenciaCotizacion(idCotizacion);
          if (existencia) applyExistenciaStocks(existencia);
        } catch (error) {
          console.error("Error obteniendo existencias para mayorista:", error);
        }
      }

      const resumenResponse = await getResumen(
        idCotizacion,
        descuentoTotalActual,
        false,
        forceFreshResumen,
      );
      setResumen(resumenResponse || {});
    } catch (error) {
      console.error("Error fetching data:", error);
      notify("error", "Error cargando datos", error.message || "");
    } finally {
      setLoading(false);
    }
  }, [
    idCotizacion,
    data.interfaz,
    data.descuentoTotal,
    setData,
    interfaz,
    existenciaStocks,
    notify,
    offlineMode,
  ]);

  useEffect(() => {
    if (!isDetalleVisible || !idCotizacion) return;

    fetchData();
    // La carga debe ejecutarse al abrir/cambiar la cotización. fetchData
    // contiene estado derivado (por ejemplo existencias) y no se usa como
    // dependencia para evitar recargas encadenadas.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idCotizacion, isDetalleVisible, offlineMode]);

  const productosTable = () => {
    setVisibleProductosTable(true);
  };

  const handleProductoAgregado = useCallback(
    async (codigoProducto) => {
      await fetchData();

      if (data.documento === "Nota de Venta" && codigoProducto != null) {
        setProductoRecienAgregado(String(codigoProducto).trim());
      }
    },
    [data.documento, fetchData],
  );

  const handleProductoDestacado = useCallback(() => {
    setProductoRecienAgregado(null);
  }, []);

  const applyExistenciaStocks = (existenciaResponse) => {
    const stocks = existenciaResponse?.stocks || {};
    setAlmacenes(existenciaResponse?.almacenes || {});
    setExistenciaStocks(stocks);
    setDetalles((prev) =>
      prev.map((line) => {
        const key = normalizeArticuloKey(line.referencia);
        const stockInfo = stocks[key];
        if (!stockInfo) return line;
        const nextStock =
          stockInfo.stock1 ?? stockInfo.stock ?? line.stock ?? 0;
        return { ...line, stock: nextStock };
      }),
    );
  };

  const removeLine = async (rowIndex) => {
    setLoading(true);
    const line = detalles[rowIndex];

    if (!line || !line.id) {
      setLoading(false);
      return;
    }

    try {
      if (offlineMode) {
        await eliminarDetalleCotizacionOffline(line.id);
      } else {
        await eliminarProducto(line.id);
      }

      await fetchData();
    } catch (err) {
      console.error("Error eliminando producto:", err);
      notify(
        "error",
        "Error eliminando producto",
        "No se pudo eliminar el producto",
      );
    } finally {
      setLoading(false);
    }
  };

  const toggleProductSelection = (id, checked) => {
    if (id == null) return;
    setSelectedProductIds((prev) =>
      checked
        ? prev.includes(id)
          ? prev
          : [...prev, id]
        : prev.filter((selectedId) => selectedId !== id),
    );
  };

  const removeSelectedProducts = async () => {
    if (selectedProductIds.length === 0) {
      notify("warn", "Selección", "Seleccione al menos un producto");
      return;
    }

    const confirmado = window.confirm(
      `¿Está seguro de que desea eliminar ${selectedProductIds.length} producto(s)?`,
    );
    if (!confirmado) return;

    try {
      setLoading(true);
      for (const id of selectedProductIds) {
        if (offlineMode) {
          await eliminarDetalleCotizacionOffline(id);
        } else {
          await eliminarProducto(id);
        }
      }
      setSelectedProductIds([]);
      await fetchData();
      notify(
        "success",
        "Productos eliminados",
        "La selección fue eliminada correctamente",
      );
    } catch (error) {
      console.error("Error eliminando productos seleccionados:", error);
      notify("error", "Error eliminando productos", error.message || "");
    } finally {
      setLoading(false);
    }
  };

  const onChangeCantidad = async (cantidad, id) => {
    try {
      setLoading(true);

      if (offlineMode) {
        await actualizarDetalleCotizacionOffline(id, {
          cantidad: cantidad ?? 0,
        });
      } else {
        await actualizarCantidad(cantidad, id);
      }

      await fetchData();
    } catch (error) {
      console.error("Error actualizando cantidad:", error);
      notify("error", "Error actualizando cantidad", error.message || "");
    } finally {
      setLoading(false);
    }
  };

  const updateLine = async (rowIndex, field, value) => {
    const line = detalles[rowIndex];
    if (!line) return;
    if (offlineMode) {
      try {
        setLoading(true);

        await actualizarDetalleCotizacionOffline(line.id, {
          [field]: value ?? 0,
        });

        await fetchData();
      } catch (error) {
        console.error("Error actualizando detalle offline:", error);
        notify("error", "Error actualizando detalle", error.message || "");
      } finally {
        setLoading(false);
      }

      return;
    }
    if (field === "descuento") {
      try {
        setLoading(true);
        await actualizarDescuento(value ?? 0, line.id);
        await fetchData();
      } catch (error) {
        console.error("Error actualizando descuento:", error);
        notify("error", "Error actualizando descuento", error.message || "");
      } finally {
        setLoading(false);
      }
    } else if (field === "precio") {
      try {
        setLoading(true);
        await actualizarPrecio(line.id, value ?? 0);
        await fetchData();
      } catch (error) {
        console.error("Error actualizando precio:", error);
        notify("error", "Error actualizando precio", error.message || "");
      } finally {
        setLoading(false);
      }
    } else if (field === "unidad") {
      try {
        setLoading(true);
        await actualizarUnidad(line.id, value ?? "");
        await fetchData();
      } catch (error) {
        console.error("Error actualizando unidad de medida:", error);
        notify("error", "Error actualizando unidad", error.message || "");
      } finally {
        setLoading(false);
      }
    } else if (field === "calzada") {
      try {
        setLoading(true);
        await actualizarCalzada(line.id, value ?? 0);
        await fetchData();
      } catch (error) {
        console.error("Error actualizando costo de venta calzada:", error);
        notify("error", "Error actualizando costo", error.message || "");
      } finally {
        setLoading(false);
      }
    } else if (field === "centro") {
      const newDetalles = [...detalles];
      newDetalles[rowIndex][field] = value;
      setDetalles(newDetalles);
      if (!idCotizacion || !line.id) return;
      try {
        setLoading(true);
        await getAlmacen(line.id, value);
        const existencia = await existenciaCotizacion(idCotizacion);
        if (existencia) applyExistenciaStocks(existencia);
        const resumenResponse = await getResumen(idCotizacion, 0, false, true);
        setResumen(resumenResponse || {});
      } catch (error) {
        console.error("Error actualizando centro:", error);
        notify("error", "Error actualizando centro", error.message || "");
      } finally {
        setLoading(false);
      }
    } else {
      const newDetalles = [...detalles];
      newDetalles[rowIndex][field] = value;
      setDetalles(newDetalles);
    }
  };

  const handleMarcarTodosCajaCompleta = (checked) => {
    setTodosCajaCompleta(checked);

    setDetalles((prevDetalles) =>
      prevDetalles.map((detalle) => ({
        ...detalle,
        cajaCompleta: checked,
      })),
    );
  };

  const searchProductos = async (event, index, type = "descripcion") => {
    try {
      const lineId = data.detalles[index]?.id || index;
      if (type === "codigo") {
        return;
      } else {
        const result = await getProductos({ descripcion: event.query });
        if (result && result.productos) {
          setSuggestions((prev) => ({ ...prev, [lineId]: result.productos }));
        }
      }
    } catch (error) {
      console.error("Error buscando productos:", error);
    }
  };

  const searchProductosBusqueda = async (event) => {
    const value = event.query;
    if (!value || value.length < 3) {
      setProductosBusqueda([]);
      return;
    }

    try {
      const result = await getProductos({ descripcion: value });
      setProductosBusqueda(result && result.productos ? result.productos : []);
    } catch (error) {
      console.error("Error buscando productos por descripción:", error);
      setProductosBusqueda([]);
    }
  };

  const validateCode = async (index, codigo) => {
    if (!codigo) return;

    const lineId = data.detalles[index]?.id || index;

    try {
      const listaPrecio = data.cliente?.listaPrecio?.codigo;
      if (!listaPrecio) {
        toast.current.show({
          severity: "warn",
          summary: "Advertencia",
          detail: "No hay lista de precios seleccionada",
        });
        return;
      }

      const result = await getPrecio(codigo, listaPrecio);

      if (result && result.codigo) {
        // Producto encontrado: autoseleccionar
        onProductSelect({ value: result }, index);
        setInvalidLines((prev) => {
          const newState = { ...prev };
          delete newState[lineId];
          return newState;
        });
      } else {
        throw new Error("Producto no encontrado");
      }
    } catch (error) {
      // Producto no encontrado o error
      setInvalidLines((prev) => ({ ...prev, [lineId]: true }));
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Producto no encontrado",
      });
    }
  };

  const onChangeCondicionEntrega = async (value) => {
    try {
      setData({ ...data, condicionEntrega: value });
      if (!idCotizacion) {
        notify("warn", "Cotización", "ID de cotización no disponible");
        return;
      }
      setLoading(true);
      const condicion = value === "DESPACHO" ? 4 : 3;

      if (offlineMode) {
        const cambios = {
          condicionEntrega: value,
          condicion: String(condicion),
        };

        if (condicion === 3) {
          cambios.direccionDespacho = "";
          cambios.referenciaDespacho = "";
          cambios.comunaDespacho = null;
          cambios.valorFlete = 0;
        } else {
          cambios.direccionDespacho =
            data.direccionDespacho ?? data?.cliente?.direccion ?? "";
          cambios.referenciaDespacho = data.referenciaDespacho ?? "";
          cambios.comunaDespacho =
            data.comunaDespacho ?? data?.cliente?.comuna?.codigo ?? null;
        }

        await actualizarCotizacionOffline(idCotizacion, cambios);
        await fetchData(true);
        return;
      }

      if (condicion === 3) {
        // Si es RETIRO, desactivamos el despacho también.
        await actualizarDespacho(idCotizacion, false);
      }
      await actualizarEntrega(idCotizacion, condicion);
      // Legacy recalculaba el resumen inmediatamente después de cambiar la
      // condición. Forzamos una lectura nueva para cargar el flete de DESPACHO.
      await fetchData(true);
    } catch (error) {
      console.error("Error actualizando condición de entrega:", error);
      notify(
        "error",
        "Error actualizando condición de entrega",
        error.message || "",
      );
    } finally {
      setLoading(false);
    }
  };

  const onProductSelect = async (e, index) => {
    const product = e.value;
    const newDetalles = [...data.detalles];

    // Actualizamos nombre y referencia (codigo) con el producto seleccionado
    newDetalles[index]["nombre"] = product.descripcion;
    newDetalles[index]["referencia"] = product.codigo;

    try {
      const listaPrecio = data.cliente?.listaPrecio?.codigo;
      if (listaPrecio) {
        const precioData = await getPrecio(product.codigo, listaPrecio);
        if (precioData && precioData.precio !== undefined) {
          newDetalles[index]["precio"] = precioData.precio;
          newDetalles[index]["precio_lista"] = precioData.precio;
        }
      }
    } catch (error) {
      console.error("Error obteniendo precio:", error);
    }

    // Si hay más campos que mapear desde el producto, se agregan aquí
    setData({ ...data, detalles: newDetalles });
  };

  return (
    <>
      <Toast ref={toast} />
      <div style={{ position: "relative" }}>
        {loading && <LoaderTransparent coverParent overlayTint="transparent" />}
        <div className="col-12">
          {/* tabla de detalle cotizacion */}
          <div className="table-responsive">
            <TableDetalleCotizacion
              idCotizacion={idCotizacion}
              detalles={detalles}
              relacionados={relacionados}
              onUpdateLine={updateLine}
              onRemoveLine={removeLine}
              suggestions={suggestions}
              onSearchProductos={searchProductos}
              onProductSelect={onProductSelect}
              onValidateCode={validateCode}
              invalidLines={invalidLines}
              interfaz={interfaz}
              proyectos={proyectos}
              proyecto={data.proyecto?.id || data.proyecto}
              onRefresh={fetchData}
              onChangeCantidad={onChangeCantidad}
              onNotify={notify}
              confirming={confirming}
              almacenes={almacenes}
              edit={edit}
              setLoading={setLoading}
              tipoDocumento={
                data.documento === "Nota de Venta" ? "nota-venta" : null
              }
              documento={data.documento}
              unidadMedidas={unidadMedidas}
              productoRecienAgregado={productoRecienAgregado}
              onProductoDestacado={handleProductoDestacado}
              onProductoAgregado={handleProductoAgregado}
              selectedProductIds={selectedProductIds}
              onToggleProductSelection={toggleProductSelection}
              onCajaCompletaChange={(checked) => {
                setDetalles((prevDetalles) =>
                  prevDetalles.map((detalle) => ({
                    ...detalle,
                    cajaCompleta: checked,
                  })),
                );
                setTodosCajaCompleta(checked);
              }}
            />
          </div>

          {/* barra de busqueda */}
          {!confirming && (
            <>
              <div className="flex flex-column md:flex-row md:align-items-center">
                <BarraBusquedaCotizacion
                  codigoBusqueda={codigoBusqueda}
                  setCodigoBusqueda={setCodigoBusqueda}
                  descripcionBusqueda={descripcionBusqueda}
                  setDescripcionBusqueda={setDescripcionBusqueda}
                  productosBusqueda={productosBusqueda}
                  searchProductosBusqueda={searchProductosBusqueda}
                  idCotizacion={idCotizacion}
                  onAdded={handleProductoAgregado}
                  onNotify={notify}
                  todosCajaCompleta={todosCajaCompleta}
                  onMarcarTodosCajaCompleta={handleMarcarTodosCajaCompleta}
                />
              </div>
            </>
          )}

          {/* observaciones, condiciones y resumen */}
          <div className="flex flex-column mt-2">
            <ObservacionesEntregaResumen
              data={data}
              setData={setData}
              idCotizacion={idCotizacion}
              notify={notify}
              resumen={resumen}
              setResumen={setResumen}
              onChangeCondicionEntrega={onChangeCondicionEntrega}
              onRefresh={(descuentoTotalActualizado) =>
                fetchData(true, descuentoTotalActualizado)
              }
            />
          </div>
          {/* botonera */}
          <div className="flex flex-column mt-3">
            <BotoneraCotizacion
              interfaz={interfaz}
              codigo={data.cliente?.codigo}
              onAgregarMaterial={productosTable}
              idCotizacion={idCotizacion}
              onRefresh={fetchData}
              onNotify={notify}
              setIsDetalleVisible={setIsDetalleVisible}
              loading={loading}
              setLoading={setLoading}
              confirming={confirming}
              setConfirming={setConfirming}
              onUpdateStocks={applyExistenciaStocks}
              edit={edit}
              tipoDocumento={
                data.documento === "Nota de Venta" ? "nota-venta" : null
              }
              descuentoTotal={Number(data.descuentoTotal || 0)}
              selectedProductIds={selectedProductIds}
              onRemoveSelectedProducts={removeSelectedProducts}
            />
          </div>
        </div>

        <TableProductos
          visible={visibleProductosTable}
          onHide={() => setVisibleProductosTable(false)}
          onCancel={() => setVisibleProductosTable(false)}
          idCotizacion={idCotizacion}
          interfaz={interfaz}
          data={data}
          onAdded={handleProductoAgregado}
          onNotify={notify}
          loading={loading}
          setLoading={setLoading}
        />
      </div>
    </>
  );
};

export default DetalleCotizacion;
